(() => {
'use strict';

class MPBirthPoster extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mpBirthReady === 'true') return;
    this.dataset.mpBirthReady = 'true';
    this.canvas = this.querySelector('[data-mp-birth-canvas]');
    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.form = this.querySelector('[data-mp-birth-form]');
    this.submitButton = this.querySelector('[data-mp-birth-submit]');
    this.outputInput = this.querySelector('[data-mp-birth-output]');
    this.outputDpiInput = this.querySelector('[data-mp-birth-output-dpi]');
    this.variantInput = this.querySelector('[data-mp-birth-variant-id]');
    this.price = this.querySelector('[data-mp-birth-price]');
    this.review = this.querySelector('[data-mp-birth-review]');
    this.error = this.querySelector('[data-mp-birth-error]');
    this.summaryInput = this.querySelector('[data-mp-birth-summary]');
    this.illustrationValue = this.querySelector('[data-mp-birth-illustration-value]');
    this.directionValue = this.querySelector('[data-mp-birth-direction-value]');
    this.backgroundValue = this.querySelector('[data-mp-birth-background-value]');
    this.inkValue = this.querySelector('[data-mp-birth-ink-value]');
    this.backgroundLabel = this.querySelector('[data-mp-birth-background-label]');
    this.inkLabel = this.querySelector('[data-mp-birth-ink-label]');
    this.textModeValue = this.querySelector('[data-mp-birth-text-mode-value]');
    this.standardFields = this.querySelector('[data-mp-birth-standard-fields]');
    this.customFields = this.querySelector('[data-mp-birth-custom-fields]');
    this.whiteInkChoice = this.querySelector('[data-mp-birth-white-ink]');
    this.previewTrigger = this.querySelector('[data-mp-birth-preview-open]');
    this.previewDialog = this.querySelector('[data-mp-birth-preview-dialog]');
    this.previewImage = this.querySelector('[data-mp-birth-preview-image]');
    this.previewClose = this.querySelector('[data-mp-birth-preview-close]');
    this.variants = JSON.parse(this.querySelector('[data-mp-birth-variants]')?.textContent || '[]');
    this.images = [];
    this.bounds = [];
    this.tintCanvas = document.createElement('canvas');
    this.tintKey = '';
    this.preparing = false;

    this.form.querySelectorAll('input,textarea').forEach((field) => {
      const eventName = field.type === 'radio' ? 'change' : 'input';
      field.addEventListener(eventName, () => this.handleChange(field));
    });
    this.form.addEventListener('submit', (event) => this.prepareSubmit(event), true);
    this.initPreviewModal();
    this.loadIllustrations();
    this.syncTextMode();
    this.resolveVariant();
    this.updateAll();
  }

  initPreviewModal() {
    if (!this.previewTrigger || !this.previewDialog || !this.previewImage) return;
    this.previewTrigger.addEventListener('click', () => this.openPreview());
    this.previewTrigger.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.openPreview();
    });
    this.previewClose?.addEventListener('click', () => this.closePreview());
    this.previewDialog.addEventListener('click', (event) => {
      if (event.target === this.previewDialog) this.closePreview();
    });
  }

  openPreview() {
    if (!this.classList.contains('is-ready')) return;
    this.syncPreviewModal();
    if (typeof this.previewDialog.showModal === 'function') this.previewDialog.showModal();
    else this.previewDialog.setAttribute('open', '');
  }

  closePreview() {
    if (typeof this.previewDialog.close === 'function' && this.previewDialog.open) this.previewDialog.close();
    else this.previewDialog.removeAttribute('open');
  }

  syncPreviewModal() {
    if (!this.previewImage || !this.images.some(Boolean)) return;
    this.previewImage.src = this.canvas.toDataURL('image/jpeg', .94);
  }

  async loadIllustrations() {
    const urls = [1, 2, 3, 4].map((number) => this.dataset[`illustration${number}`]);
    const thumbnails = [...this.querySelectorAll('.mp-birth-poster__illustration-card img')];
    const results = await Promise.allSettled(urls.map((url, index) => this.loadImage(url, thumbnails[index])));
    this.images = results.map((result) => result.status === 'fulfilled' ? result.value : null);
    this.bounds = this.images.map((image, index) => image ? this.getIllustrationBounds(image, index) : null);

    results.forEach((result, index) => {
      const input = this.querySelector(`[data-mp-birth-illustration][value="${index + 1}"]`);
      if (!input) return;
      const unavailable = result.status === 'rejected';
      input.disabled = unavailable;
      input.closest('.mp-birth-poster__illustration-card')?.classList.toggle('is-unavailable', unavailable);
    });

    const firstLoadedIndex = this.images.findIndex(Boolean);
    if (firstLoadedIndex < 0) {
      this.showError('Illustrationen kunde inte laddas. Kontrollera bildlänkarna under Shopify Innehåll → Filer.');
      return;
    }

    const selectedInput = this.selected('[data-mp-birth-illustration]');
    if (!selectedInput || !this.images[Number(selectedInput.value) - 1]) {
      const fallbackInput = this.querySelector(`[data-mp-birth-illustration][value="${firstLoadedIndex + 1}"]`);
      if (fallbackInput) fallbackInput.checked = true;
    }

    this.classList.add('is-ready');
    if (results.some((result) => result.status === 'rejected')) {
      this.showError('Någon illustration kunde inte laddas, men de tillgängliga illustrationerna går att använda.');
    } else {
      this.clearError();
    }
    this.updateAll();
  }

  loadImage(url, existingImage = null) {
    return new Promise((resolve, reject) => {
      const image = existingImage || new Image();
      if (image.complete && image.naturalWidth > 0) {
        resolve(image);
        return;
      }
      if (image.complete && image.getAttribute('src')) {
        reject(new Error(`Kunde inte ladda: ${url}`));
        return;
      }
      image.decoding = 'async';
      const timeout = window.setTimeout(() => reject(new Error(`Timeout: ${url}`)), 20000);
      image.onload = () => { window.clearTimeout(timeout); resolve(image); };
      image.onerror = () => { window.clearTimeout(timeout); reject(new Error(`Kunde inte ladda: ${url}`)); };
      if (!image.getAttribute('src')) image.src = url;
    });
  }

  getIllustrationBounds(image, index) {
    const normalizedBounds = [
      { x: 401 / 1462, y: 163 / 2048, width: 660 / 1462, height: 1566 / 2048 },
      { x: 419 / 1462, y: 169 / 2048, width: 682 / 1462, height: 1562 / 2048 },
      { x: 476 / 1462, y: 166 / 2048, width: 512 / 1462, height: 1583 / 2048 },
      { x: 499 / 1462, y: 155 / 2048, width: 497 / 1462, height: 1587 / 2048 }
    ][index];
    return {
      x: Math.round(image.naturalWidth * normalizedBounds.x),
      y: Math.round(image.naturalHeight * normalizedBounds.y),
      width: Math.round(image.naturalWidth * normalizedBounds.width),
      height: Math.round(image.naturalHeight * normalizedBounds.height)
    };
  }

  handleChange(field) {
    this.clearError();
    if (field.matches('[data-mp-birth-text-mode]')) this.syncTextMode();
    if (field.matches('[data-mp-birth-background]')) this.syncWhiteInk();
    if (field.matches('[data-mp-birth-option]')) this.resolveVariant();
    if (field.matches('[data-mp-birth-illustration],[data-mp-birth-ink]')) this.tintKey = '';
    this.updateAll();
  }

  selected(selector) { return this.querySelector(`${selector}:checked`); }
  value(selector, fallback = '') { return this.querySelector(selector)?.value.trim() || fallback; }

  syncTextMode() {
    const mode = this.selected('[data-mp-birth-text-mode]')?.value || 'standard';
    this.standardFields.hidden = mode !== 'standard';
    this.customFields.hidden = mode !== 'custom';
    this.standardFields.querySelectorAll('input').forEach((field) => { field.disabled = mode !== 'standard'; });
    this.customFields.querySelectorAll('textarea').forEach((field) => { field.disabled = mode !== 'custom'; field.required = mode === 'custom'; });
  }

  syncWhiteInk() {
    const background = this.selected('[data-mp-birth-background]')?.value || '#ffffff';
    const rgb = this.hexToRgb(background);
    const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    const disabled = luminance > 0.78;
    this.whiteInkChoice.classList.toggle('is-disabled', disabled);
    const whiteInput = this.whiteInkChoice.querySelector('input');
    whiteInput.disabled = disabled;
    if (disabled && whiteInput.checked) {
      const black = this.querySelector('[data-mp-birth-ink][value="#171717"]');
      black.checked = true;
      this.tintKey = '';
    }
  }

  hexToRgb(hex) {
    const clean = String(hex).replace('#', '');
    const number = parseInt(clean.length === 3 ? clean.split('').map((value) => value + value).join('') : clean, 16);
    return { r: number >> 16, g: (number >> 8) & 255, b: number & 255 };
  }

  getSelectedOptions() {
    return [...this.querySelectorAll('[data-mp-birth-option]:checked')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
  }

  resolveVariant() {
    if (!this.variants.length) return;
    const selectedOptions = this.getSelectedOptions().map((input) => input.value);
    const variant = this.variants.find((item) => selectedOptions.length ? item.options.every((value, index) => value === selectedOptions[index]) : item.id === Number(this.variantInput.value)) || this.variants[0];
    if (!variant) return;
    this.variantInput.value = variant.id;
    if (this.price && variant.price) this.price.textContent = variant.price;
    this.submitButton.dataset.variantAvailable = String(variant.available !== false);
  }

  getState() {
    const illustration = this.selected('[data-mp-birth-illustration]');
    const direction = this.selected('[data-mp-birth-direction]');
    const background = this.selected('[data-mp-birth-background]');
    const ink = this.selected('[data-mp-birth-ink]');
    const textMode = this.selected('[data-mp-birth-text-mode]');
    return {
      illustration: Number(illustration?.value || 1), illustrationLabel: illustration?.dataset.label || 'Ren skiss',
      direction: direction?.value || 'left', directionLabel: direction?.dataset.label || 'Vänster',
      background: background?.value || '#ffffff', backgroundLabel: background?.dataset.label || 'Vit',
      ink: ink?.value || '#171717', inkLabel: ink?.dataset.label || 'Svart',
      textMode: textMode?.value || 'standard', textModeLabel: textMode?.dataset.label || 'Födelseuppgifter',
      name: this.value('[data-mp-birth-name]'), date: this.value('[data-mp-birth-date]'), time: this.value('[data-mp-birth-time]'),
      weight: this.value('[data-mp-birth-weight]'), length: this.value('[data-mp-birth-length]', '51'), place: this.value('[data-mp-birth-place]'),
      customText: this.value('[data-mp-birth-custom-text]')
    };
  }

  updateAll() {
    const state = this.getState();
    this.illustrationValue.value = state.illustrationLabel;
    this.directionValue.value = state.directionLabel;
    this.backgroundValue.value = `${state.backgroundLabel} (${state.background})`;
    this.inkValue.value = `${state.inkLabel} (${state.ink})`;
    if (this.backgroundLabel) this.backgroundLabel.textContent = state.backgroundLabel;
    if (this.inkLabel) this.inkLabel.textContent = state.inkLabel;
    this.textModeValue.value = state.textModeLabel;
    this.summaryInput.value = this.getSummary(state);
    this.drawPreview();
    this.updateReview(state);
    this.updateButton(state);
  }

  getSummary(state) {
    const text = state.textMode === 'standard'
      ? `${state.name || 'Ej ifyllt'} | ${state.date || 'Ej ifyllt'} kl. ${state.time || 'Ej ifyllt'} | ${state.weight || '–'} g | ${state.length || '–'} cm${state.place ? ` | ${state.place}` : ''}`
      : state.textMode === 'custom' ? state.customText || 'Ej ifyllt' : 'Ingen text';
    return `Illustration: ${state.illustrationLabel} | Riktning: ${state.directionLabel} | Bakgrund: ${state.backgroundLabel} | Illustrationsfärg: ${state.inkLabel} | Text: ${text}`;
  }

  isValid(state = this.getState()) {
    if (!this.images.some(Boolean) || this.submitButton.dataset.variantAvailable === 'false') return false;
    if (state.textMode === 'custom') return Boolean(state.customText);
    if (state.textMode === 'none') return true;
    return Boolean(state.name && state.date && state.time && state.weight && state.length);
  }

  updateButton(state = this.getState()) { this.submitButton.disabled = this.preparing || !this.isValid(state); }

  updateReview(state = this.getState()) {
    const rows = [
      ['Storlek', '50×70 cm'],
      ['Illustration', state.illustrationLabel], ['Riktning', state.directionLabel],
      ['Bakgrund', state.backgroundLabel], ['Illustrationsfärg', state.inkLabel], ['Text', state.textModeLabel]
    ];
    if (state.textMode === 'standard' && state.name) rows.push(['Namn', state.name]);
    if (state.textMode === 'custom' && state.customText) rows.push(['Egen text', state.customText]);
    this.review.innerHTML = rows.map(([label, value]) => `<li><span>${this.escapeHtml(label)}</span><strong>${this.escapeHtml(value)}</strong></li>`).join('');
  }

  drawPreview() {
    if (!this.images.some(Boolean)) return;
    this.drawPoster(this.ctx, this.canvas.width, this.canvas.height, this.getState());
    if (this.previewDialog?.open) this.syncPreviewModal();
  }

  drawPoster(context, width, height, state) {
    const unit = width / 500;
    const mm = (value) => value * unit;
    context.save();
    context.clearRect(0, 0, width, height);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.fillStyle = state.background;
    context.fillRect(mm(40), mm(40), mm(420), mm(585));

    let imageIndex = Math.max(0, Math.min(3, state.illustration - 1));
    if (!this.images[imageIndex]) imageIndex = this.images.findIndex(Boolean);
    if (imageIndex < 0) { context.restore(); return; }
    const image = this.images[imageIndex];
    const bounds = this.bounds[imageIndex];
    const childHeightMm = Math.min(550, Math.max(200, (Number(state.length) || 51) * 10));
    const childWidthMm = childHeightMm * bounds.width / bounds.height;
    const maxWidthMm = 390;
    const fittedWidthMm = Math.min(childWidthMm, maxWidthMm);
    const fittedHeightMm = fittedWidthMm === childWidthMm ? childHeightMm : childHeightMm * maxWidthMm / childWidthMm;
    const xMm = 250 - fittedWidthMm / 2;
    const yMm = 40 + (585 - fittedHeightMm) / 2;
    const tinted = this.getTintedIllustration(imageIndex, state.ink);
    context.save();
    if (state.direction === 'right') {
      context.translate(width, 0);
      context.scale(-1, 1);
    }
    context.drawImage(tinted, 0, 0, tinted.width, tinted.height, mm(xMm), mm(yMm), mm(fittedWidthMm), mm(fittedHeightMm));
    context.restore();

    if (state.textMode === 'standard') this.drawStandardText(context, width, unit, state);
    if (state.textMode === 'custom') this.drawCustomText(context, width, unit, state.customText || 'Skriv din text här');
    context.restore();
  }

  getTintedIllustration(index, color) {
    const key = `${index}|${color}`;
    if (this.tintKey === key) return this.tintCanvas;
    const image = this.images[index];
    const bounds = this.bounds[index];
    this.tintCanvas.width = bounds.width;
    this.tintCanvas.height = bounds.height;
    const context = this.tintCanvas.getContext('2d');
    context.clearRect(0, 0, bounds.width, bounds.height);
    context.drawImage(image, -bounds.x, -bounds.y);
    context.globalCompositeOperation = 'source-in';
    context.fillStyle = color;
    context.fillRect(0, 0, bounds.width, bounds.height);
    context.globalCompositeOperation = 'source-over';
    this.tintKey = key;
    return this.tintCanvas;
  }

  drawStandardText(context, width, unit, state) {
    const center = width / 2;
    context.fillStyle = '#191919';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    const hasPlace = Boolean(state.place);
    const name = (state.name || 'BARNETS NAMN').toLocaleUpperCase('sv-SE');
    this.setFittedFont(context, name, 15.5 * unit, 9.5 * unit, 420 * unit, 700);
    context.fillText(name, center, (hasPlace ? 645 : 652) * unit);
    const date = this.formatDate(state.date) || '14 MAJ 2026';
    const details = [date, state.time ? `KL. ${state.time}` : 'KL. 08:42', state.weight ? `${state.weight} G` : '3520 G', state.length ? `${String(state.length).replace('.', ',')} CM` : '51 CM'].join('  /  ');
    this.setFittedFont(context, details, 7.2 * unit, 5.2 * unit, 430 * unit, 600);
    context.fillText(details, center, (hasPlace ? 668 : 673) * unit);
    if (state.place) {
      const place = state.place.toLocaleUpperCase('sv-SE');
      this.setFittedFont(context, place, 5.6 * unit, 4.2 * unit, 420 * unit, 600);
      context.fillText(place, center, 690 * unit);
    }
  }

  drawCustomText(context, width, unit, text) {
    context.fillStyle = '#191919';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    const lines = this.wrapText(context, text, 420 * unit, 12.5 * unit, 7 * unit, 600, 2);
    const lineHeight = 15 * unit;
    const startY = 662 * unit - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, index) => context.fillText(line, width / 2, startY + index * lineHeight));
  }

  font(size, weight = 400) { return `${weight} ${size}px "Helvetica Neue",Arial,sans-serif`; }

  setFittedFont(context, text, startSize, minimumSize, maxWidth, weight) {
    let size = startSize;
    context.font = this.font(size, weight);
    while (size > minimumSize && context.measureText(text).width > maxWidth) {
      size -= .25;
      context.font = this.font(size, weight);
    }
  }

  wrapText(context, text, maxWidth, startSize, minimumSize, weight, maxLines) {
    let size = startSize;
    let lines = [];
    do {
      context.font = this.font(size, weight);
      const words = String(text).trim().split(/\s+/);
      lines = [];
      let line = '';
      words.forEach((word) => {
        const test = line ? `${line} ${word}` : word;
        if (line && context.measureText(test).width > maxWidth) { lines.push(line); line = word; }
        else line = test;
      });
      if (line) lines.push(line);
      size -= .25;
    } while (lines.length > maxLines && size >= minimumSize);
    context.font = this.font(Math.max(minimumSize, size + .25), weight);
    if (lines.length > maxLines) lines = [lines.slice(0, -1).join(' '), lines[lines.length - 1]];
    return lines.slice(0, maxLines);
  }

  formatDate(value) {
    if (!value) return '';
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return value;
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).toLocaleUpperCase('sv-SE');
  }

  async prepareSubmit(event) {
    if (this.preparing) return;
    event.preventDefault();
    const state = this.getState();
    if (!this.isValid(state)) {
      this.showError('Fyll i alla obligatoriska uppgifter innan du beställer.');
      this.form.reportValidity();
      return;
    }
    this.preparing = true;
    this.submitButton.disabled = true;
    const defaultText = this.submitButton.dataset.defaultText || 'Lägg i varukorgen';
    this.submitButton.textContent = 'Skapar tryckfil…';
    try {
      const result = await this.createPrintFile(state);
      const transfer = new DataTransfer();
      transfer.items.add(result.file);
      this.outputInput.files = transfer.files;
      this.outputDpiInput.value = `${result.dpi} DPI`;
      this.form.submit();
    } catch (error) {
      this.preparing = false;
      this.submitButton.textContent = defaultText;
      this.updateButton(state);
      this.showError('Tryckfilen kunde inte skapas på den här enheten. Prova gärna igen eller använd en annan webbläsare.');
    }
  }

  async createPrintFile(state) {
    const sizes = [
      { dpi: 300, width: 5906, height: 8268 },
      { dpi: 200, width: 3937, height: 5512 },
      { dpi: 150, width: 2953, height: 4134 }
    ];
    for (const size of sizes) {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size.width;
        canvas.height = size.height;
        const context = canvas.getContext('2d', { alpha: false });
        if (!context) continue;
        this.drawPoster(context, canvas.width, canvas.height, state);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', .94));
        canvas.width = 1;
        canvas.height = 1;
        if (!blob) continue;
        return { dpi: size.dpi, file: new File([blob], `fodelsetavla-${Date.now()}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }) };
      } catch (error) { /* Try a smaller, safer canvas. */ }
    }
    throw new Error('No supported print canvas size');
  }

  showError(message) { this.error.textContent = message; }
  clearError() { this.error.textContent = ''; }
  escapeHtml(value) { const element = document.createElement('div'); element.textContent = value; return element.innerHTML; }
}

if (!customElements.get('mp-birth-poster')) customElements.define('mp-birth-poster', MPBirthPoster);
})();